import CheckIcon from "@mui/icons-material/Check";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { CreditStatus, PaymentPlansDetail } from "../../Models/Credit";
import {
  INK,
  SOFT_BG,
  SUCCESS,
  SUCCESS_BG,
  TEXT_MUTED,
} from "../../Utils/designTokens";
import { getRemoteValue } from "../../Utils/firebase";
import CCreditStatusLabel from "../CCreditStatusLabel/CCreditStatusLabel";
import { FormatAmountSymbol } from "../FormatData/FormatAmount";
import FormatDateToTR from "../FormatData/FormatDate";

interface Props {
  debtDetail?: PaymentPlansDetail;
  /** CRL-3241: detail API has no shop info; the card passes it for the modal header. */
  shop?: {
    id: string;
    name: string;
    logoUrl: string;
  };
}

/**
 * CRL-3241: redesigned completed payment detail (completed-payment-details.png).
 * Only fields that already exist in `PaymentPlansDetail` are shown; the mock's
 * "Bu harcamaya itiraz et" link is intentionally absent (no dispute logic).
 */
const DebtDetail = ({ debtDetail, shop }: Props) => {
  if (!debtDetail) return null;

  const isPaid = debtDetail.status === CreditStatus.PAID;

  return (
    <Box>
      {/* Header: shop + amount · date */}
      <Stack direction={"row"} alignItems={"center"} gap={"14px"} pr={5}>
        {shop?.logoUrl && (
          <img
            src={shop.logoUrl}
            alt={shop.name}
            style={{
              objectFit: "cover",
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              flexShrink: 0,
            }}
          />
        )}
        <Stack sx={{ minWidth: 0 }}>
          <Typography
            lineHeight={"normal"}
            fontSize={"18px"}
            fontWeight={700}
            color={INK}
            noWrap
          >
            {shop?.name ?? getRemoteValue("payment_details")}
          </Typography>
          <Typography
            lineHeight={"normal"}
            fontSize={"14px"}
            color={TEXT_MUTED}
            pt={"4px"}
          >
            {FormatAmountSymbol(debtDetail.totalPaidAmount)} ·{" "}
            {FormatDateToTR(debtDetail.createdAt)}
          </Typography>
        </Stack>
      </Stack>

      {/* Son Ödeme Tarihi | İşlem No */}
      <Stack direction={"row"} pt={3} gap={2}>
        <Stack sx={{ flex: 1 }}>
          <Typography fontSize={"13px"} color={TEXT_MUTED} lineHeight={"normal"}>
            {getRemoteValue("last_payment_date")}
          </Typography>
          <Typography
            fontSize={"17px"}
            fontWeight={700}
            color={INK}
            lineHeight={"normal"}
            pt={"6px"}
          >
            {FormatDateToTR(debtDetail.dueDate)}
          </Typography>
        </Stack>
        <Stack sx={{ flex: 1 }}>
          <Typography fontSize={"13px"} color={TEXT_MUTED} lineHeight={"normal"}>
            {getRemoteValue("transaction_number")}
          </Typography>
          <Typography
            fontSize={"17px"}
            fontWeight={700}
            color={INK}
            lineHeight={"normal"}
            pt={"6px"}
          >
            {debtDetail.creditId}
          </Typography>
        </Stack>
      </Stack>

      {/* Ödenen Tutar */}
      <Stack pt={3}>
        <Typography fontSize={"13px"} color={TEXT_MUTED} lineHeight={"normal"}>
          {getRemoteValue("total_amount")}
        </Typography>
        <Typography
          fontSize={"26px"}
          fontWeight={800}
          color={INK}
          lineHeight={"normal"}
          pt={"6px"}
        >
          {FormatAmountSymbol(debtDetail.totalPaidAmount)}
        </Typography>
      </Stack>

      {/* Breakdown */}
      <Box
        sx={{
          backgroundColor: SOFT_BG,
          border: "1px solid #F1F5F9",
          borderRadius: "12px",
          mt: 3,
        }}
      >
        <DetailRow
          label={getRemoteValue("shopping_amount")}
          value={FormatAmountSymbol(debtDetail.creditAmount)}
        />
        <Divider sx={{ borderColor: "#EDF2F7" }} />
        <DetailRow
          label={getRemoteValue("shopping_date")}
          value={FormatDateToTR(debtDetail.createdAt)}
        />
        <Divider sx={{ borderColor: "#EDF2F7" }} />
        <DetailRow
          label={getRemoteValue("service_fee")}
          value={FormatAmountSymbol(debtDetail.totalPaidServiceFee)}
        />
        {debtDetail.totalPaidLateAmount !== 0 && (
          <>
            <Divider sx={{ borderColor: "#EDF2F7" }} />
            <DetailRow
              label={getRemoteValue("late_payment_expense")}
              value={FormatAmountSymbol(debtDetail.totalPaidLateAmount)}
            />
          </>
        )}
      </Box>

      {/* Status */}
      {isPaid ? (
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"center"}
          gap={"8px"}
          sx={{
            backgroundColor: SUCCESS_BG,
            color: SUCCESS,
            borderRadius: "12px",
            mt: 3,
            py: "14px",
          }}
        >
          <CheckIcon sx={{ fontSize: 18 }} />
          <Typography fontSize={"15px"} fontWeight={600} color={SUCCESS}>
            <CCreditStatusLabelText status={debtDetail.status} />
          </Typography>
        </Stack>
      ) : (
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"center"}
          sx={{ mt: 3 }}
        >
          <CCreditStatusLabel status={debtDetail.status} />
        </Stack>
      )}
    </Box>
  );
};

const CCreditStatusLabelText = ({ status }: { status: CreditStatus }) => {
  // Text-only variant of CCreditStatusLabel for the full-width status banner.
  switch (status) {
    case CreditStatus.PAID:
      return <>Ödendi</>;
    default:
      return <>{status}</>;
  }
};

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"center"}
      sx={{ px: "16px", py: "14px" }}
    >
      <Typography fontSize={"14px"} color={TEXT_MUTED} lineHeight={"normal"}>
        {label}
      </Typography>
      <Typography
        fontSize={"14px"}
        fontWeight={700}
        color={INK}
        lineHeight={"normal"}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default DebtDetail;
